import {
	DownloadPersonalInfoButton,
	Dropdown,
	UploadExcelButton,
} from '@/components';
import {
	ExcelBatchOperationEnum,
	PageTypeEnum,
	TableSearchTypeMap,
	TableTypes,
	TUploadExcelPage,
} from '@/constant';
import { useGetListData, useSearchFilterSubmit } from '@/hooks';
import {
	arrayToJson,
	downloadExcelLocal,
	formatDate,
	getTranslatedMessage,
	insertObjectIfElse,
	insertObjectIfV2,
	map,
	TDownloadExcelLocal,
} from '@/utils';
import {
	KeyboardEvent,
	memo,
	MouseEvent,
	ReactNode,
	useCallback,
	useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';

import type { MenuProps } from 'antd/es/menu/';
import type { ItemType } from 'antd/es/menu/interface';

import { Column } from 'exceljs';
import { TFunction } from 'i18next';
import { has, includes, keys, length } from 'ramda';
import { isNilOrEmpty, isNonEmptyArray, isNotNilOrEmpty } from 'ramda-adjunct';

import { DropdownProps } from '../../dropdown';
import { EditableCellType, TableProps } from '../type';

type TExcelBatchOperationItem = ItemType & {
	key: ExcelBatchOperationEnum;
	label?: ReactNode;
	action?: any;
	callback?: () => void;
	trigger?: (reason: string, callback?: () => void) => void;
};

export type TExcelBatchOperation = DropdownProps & {
	excelBatchOperation?: Array<TExcelBatchOperationItem>;
	onExcelBatchChange?: (
		value: ExcelBatchOperationEnum,
		option: TExcelBatchOperationItem | TExcelBatchOperationItem[]
	) => void;
	mergeHeader?: boolean;
	excelBatchOperationIgnoreColumns?: string[];
} & Pick<TableProps, 'tableType' | 'rowKey' | 'rowSelection' | 'columns'>;

const ExcelTypeUploadMap: { [key in string]: TUploadExcelPage } = {
	[ExcelBatchOperationEnum.UPLOAD_APPLICANT_LIST]: PageTypeEnum.APPLICANT,
	[ExcelBatchOperationEnum.UPLOAD_FRANCHISE_LIST]: PageTypeEnum.FRANCHISE,
	[ExcelBatchOperationEnum.UPLOAD_RECOMMENDER_LIST]: PageTypeEnum.RECOMMENDER,
	[ExcelBatchOperationEnum.UPLOAD_LODGING_LIST]: PageTypeEnum.LODGING,
	[ExcelBatchOperationEnum.UPLOAD_ASSIGNMENT_LIST]: PageTypeEnum.ASSIGNMENT,
	[ExcelBatchOperationEnum.UPLOAD_PUBLIC_ASSIGNMENT_LIST]:
		PageTypeEnum.PUBLIC_ASSIGNMENT,
};

const ExcelNameDownloadMap: { [key in string]: string } = {
	[ExcelBatchOperationEnum.DOWNLOAD_APPLICANT_LIST]: '근로자',
	[ExcelBatchOperationEnum.DOWNLOAD_FRANCHISE_LIST]: '사업장',
	[ExcelBatchOperationEnum.DOWNLOAD_RECOMMENDER_LIST]: '추천자 목록',
	[ExcelBatchOperationEnum.DOWNLOAD_LODGING_LIST]: '숙소 목록',
	[ExcelBatchOperationEnum.DOWNLOAD_ASSIGNMENT_LIST]: '배정',
	[ExcelBatchOperationEnum.DOWNLOAD_PROGRAM_LIST]: '프로그램',
	[ExcelBatchOperationEnum.DOWNLOAD_UNAUTHORIZED_DEPARTURE]: '무단이탈',
	[ExcelBatchOperationEnum.DOWNLOAD_PAYROLL]: '급여',
	[ExcelBatchOperationEnum.DOWNLOAD_WORK_CHANGE_LIST]: '근무처변경',
	[ExcelBatchOperationEnum.DOWNLOAD_PUBLIC_ASSIGNMENT_LIST]: '공공형배정',
	[ExcelBatchOperationEnum.DOWNLOAD_PUBLIC_SALARY_LIST]: '공공형 운영 현황',
};

const getMergeCell = (
	columns: TExcelBatchOperation['columns']
): TDownloadExcelLocal['mergeCell'] => {
	let mergeCell: TDownloadExcelLocal['mergeCell'] = [];
	if (columns && isNonEmptyArray(columns)) {
		let from = 0;
		columns?.forEach((col) => {
			if (isNonEmptyArray(col?.children) && mergeCell) {
				const childrenLength = col?.children?.filter(
					(col) => !col?.hidden
				)?.length;

				if (childrenLength === 0) return;

				const to = childrenLength === 1 ? from : from + (childrenLength - 1);

				mergeCell = [
					...mergeCell,
					{
						from,
						to,
						name: (col?.exportTitle || col?.title) as string,
					},
				];

				from = to + 1;
			}
		});
	}

	return mergeCell;
};

const ExcelBatchOperation = ({
	excelBatchOperation,
	onExcelBatchChange,
	tableType,
	label = '엑셀 일괄 작업',
	...rest
}: TExcelBatchOperation) => {
	if (isNilOrEmpty(excelBatchOperation)) {
		return null;
	}

	const { t } = useTranslation('common');

	const { onSubmit } = useSearchFilterSubmit({
		searchFilterType: TableSearchTypeMap[tableType as TableTypes],
	});

	const { params } = useGetListData({
		type: tableType,
	});

	const _columns = useMemo(() => {
		const mapColumns = (col: any) => {
			if (rest?.excelBatchOperationIgnoreColumns?.includes(col?.dataIndex)) {
				return null;
			}
			if (Array.isArray(col?.children) && col?.children?.length > 0) {
				const nextChildren = col?.children.map(mapColumns).filter(Boolean);
				if (nextChildren.length === 0) return null;
				return {
					...col,
					children: nextChildren,
				};
			}
			return col;
		};

		return (rest?.columns || []).map(mapColumns).filter(Boolean) as any[];
	}, [rest?.columns, rest?.excelBatchOperationIgnoreColumns]);

	const getFileNameDownloadExcel = useCallback(
		(type: string, hasSuffix = false) => {
			return `엑셀목록_${ExcelNameDownloadMap[type]}_${formatDate(new Date(), { outputFormat: 'YYYYMMDDHHmm' })}${hasSuffix ? '.xlsx' : ''}`;
		},
		[]
	);

	const handleDownloadExcel = useCallback(
		async (type: string, dataSource?: any[]) => {
			if (!_columns) return;

			const columnsMap = (col: (typeof _columns)[number] | undefined) => ({
				header: col?.exportTitle || col?.title,
				key: col?.key,
				width: 30,

				// key to convert with options
				dataIndex: col?.dataIndex,
				cellType: col?.cellType,
				dropdownProps: col?.dropdownProps,
			});

			let columns: Array<Partial<Column>> = [];

			let mergeCell: TDownloadExcelLocal['mergeCell'] = [];

			if (!rest?.mergeHeader) {
				columns = _columns
					?.filter((col) => !col.hidden)
					.map(columnsMap) as Array<Partial<Column>>;
			} else {
				columns = _columns
					?.flatMap((col) => col?.children || col)
					.filter((col) => !col?.hidden)
					.map(columnsMap) as Array<Partial<Column>>;

				mergeCell = getMergeCell(_columns);
			}

			const selectedRowKeys = rest.rowSelection?.selectedRowKeys || [];

			const rows = length(selectedRowKeys)
				? dataSource?.filter((row) =>
						selectedRowKeys?.includes(row[(rest?.rowKey as string) || 'id'])
					)
				: dataSource;

			const fileName = getFileNameDownloadExcel(type);

			await downloadExcelLocal({
				columns,
				rows: getRowsToDownload({ rows, columns, t }),
				fileName,
				...insertObjectIfV2(isNonEmptyArray(mergeCell), {
					mergeCell,
				}),
			});
		},
		[
			_columns,
			rest.rowSelection,
			rest.rowKey,
			rest.mergeHeader,
			t,
			getFileNameDownloadExcel,
		]
	);

	const handleDownloadExcelLocal = useCallback(
		(type: string, reason: string, callback?: () => void) => {
			const fileName = getFileNameDownloadExcel(type, true);
			onSubmit?.(
				{
					...params,
					allData: true,
					downloadReason: reason,
					downloadFileName: fileName,
				},
				async (callbackProps) => {
					if (callbackProps?.error) return;
					await handleDownloadExcel(type, callbackProps?.data);
					callback?.();
				}
			);
		},
		[params, getFileNameDownloadExcel, onSubmit, handleDownloadExcel]
	);

	const _excelBatchOperation = useMemo(() => {
		return excelBatchOperation?.map((excelItem) => {
			const isUploadExcel = includes(excelItem.key, keys(ExcelTypeUploadMap));
			return {
				...excelItem,
				label: isUploadExcel ? (
					<UploadExcelButton
						pageType={ExcelTypeUploadMap[excelItem.key]}
						labelButton={excelItem?.label as string}
						buttonType="div"
						triggerClassName="!py-[7px] !px-3"
						uploadExcelSuccessCallback={() => excelItem?.callback?.()}
					/>
				) : (
					<DownloadPersonalInfoButton
						asChild
						childrenClassName="!py-[7px] !px-3"
						onDownload={(reason, callback) => {
							if (typeof excelItem?.trigger !== 'undefined') {
								excelItem?.trigger(reason, callback);
							} else {
								handleDownloadExcelLocal(excelItem.key, reason, callback);
							}
						}}
					>
						{excelItem?.label}
					</DownloadPersonalInfoButton>
				),
				className: '!p-0',
			};
		});
	}, [excelBatchOperation, handleDownloadExcelLocal]);

	const onClick: MenuProps['onClick'] = useCallback(
		(props: {
			key: string;
			domEvent: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>;
			item: any;
		}) => {
			// if (props?.item?.props?.trigger) {
			// 	props?.item?.props?.trigger();
			// 	return;
			// }

			const itemSelected = _excelBatchOperation?.find(
				(e: TExcelBatchOperationItem) => e.key === props?.key
			);
			if (itemSelected) {
				if (has('action', itemSelected)) {
					onExcelBatchChange?.(
						props?.key as ExcelBatchOperationEnum,
						itemSelected
					);

					itemSelected?.action?.();
				} else {
					if (includes(props?.key, keys(ExcelNameDownloadMap))) {
						props.domEvent.preventDefault();
					} else {
						props.domEvent.stopPropagation();
					}
				}
			}
		},
		[_excelBatchOperation, onExcelBatchChange]
	);

	return (
		<Dropdown
			label={label}
			menuProps={{
				onClick,
				items: _excelBatchOperation,
			}}
			{...rest}
		/>
	);
};

export default memo(ExcelBatchOperation);

const getRowsToDownload = ({
	columns = [],
	rows = [],
	t,
}: {
	columns: any[];
	rows?: any[];
	t?: TFunction<'common', undefined>;
}) => {
	let data = {} as any;

	for (const column of columns) {
		if (!includes(column?.cellType, [EditableCellType.DROPDOWN])) continue;

		data = {
			...data,
			...insertObjectIfElse(
				column?.dropdownProps?.options,
				{
					[column?.dataIndex]: arrayToJson(
						column?.dropdownProps?.options ?? [],
						'value'
					),
				},
				{
					[column?.dataIndex]: (record: any) =>
						arrayToJson(
							column?.dropdownProps?.optionsByRecord?.(record) ?? [],
							'value'
						),
				}
			),
		};
	}

	const dataKeys = Object.keys(data) || [];

	const getLabelOptions = (row: any) => {
		let dataWithLabel = {};

		for (const dataKey of dataKeys) {
			const dataValue = data?.[dataKey];
			const rowDataKey = row?.[dataKey];
			dataWithLabel = {
				...dataWithLabel,
				[dataKey]: (() => {
					if (isNotNilOrEmpty(rowDataKey) && t) {
						let label = rowDataKey;
						if (typeof dataValue === 'function') {
							label = dataValue(row)?.[rowDataKey]?.label || rowDataKey;
						} else {
							label = dataValue?.[rowDataKey]?.label || rowDataKey;
						}
						return getTranslatedMessage(t, label);
					}
					return rowDataKey || '';
				})(),
			};
		}

		return dataWithLabel;
	};

	const results = map(rows, (row) => ({
		...row,
		...getLabelOptions(row),
	}));

	return results;
};
